import "./Disclaimer.css";
import { useLang } from "../../../utils/i18n";

const Disclaimer = () => {
  const lang = useLang();
  if (lang === "en") {
    return (
      <section className="legal-page disclaimer-page">
        <h1 className="main-heading">Legal Disclaimer</h1>

        <div className="disclaimer-content">
          <p>
            <strong>Last updated:</strong> September 2026
            <br />
            <strong>Legal Entity:</strong> Monetique Estratégia e Gestão Financeira Ltda.
            <br />
            <strong>Website:</strong>{" "}
            <a href="https://www.monetiquepro.com" target="_blank" rel="noopener noreferrer">
              www.monetiquepro.com
            </a>
          </p>

          <p>
            The content presented on this website, as well as the information shared during scheduled meetings, is intended to provide strategic, financial and managerial guidance based on professional experience and best practice. Monetique is committed to offering accurate and well founded recommendations to support smarter decisions and sustainable business performance.
          </p>

          <h2>1. Nature of Professional Guidance</h2>
          <p>
            The information provided by Monetique, whether through the website, written materials, consulting, or strategic sessions, is <strong>advisory in nature</strong>. Although based on technical knowledge and experience, this guidance should always be evaluated in light of each client's specific context.
          </p>
          <p>Monetique works to provide valuable recommendations, but:</p>
          <ul>
            <li>Does not guarantee specific financial, legal, or business outcomes</li>
            <li>Acknowledges that results depend on external and operational factors beyond our control</li>
            <li>Reinforces that decisions made based on recommendations are the sole responsibility of the client</li>
          </ul>
          <p>
            Whenever a situation requires a formal, binding, or regulated opinion (such as a formal legal opinion, mandatory tax analysis, or issuance of regulatory documents), <strong>we recommend that the client consult licensed professionals</strong> in legal, tax, accounting, or financial fields as needed.
          </p>

          <h2>2. No Guarantee of Results</h2>
          <p>Strategies, models and insights provided by Monetique are designed to enhance decision making, but outcomes may vary depending on:</p>
          <ul>
            <li>Market conditions</li>
            <li>Operational execution</li>
            <li>External economic factors</li>
            <li>Company internal policies</li>
          </ul>
          <p>Therefore, Monetique <strong>does not guarantee</strong>:</p>
          <ul>
            <li>Business growth or performance</li>
            <li>Specific financial results</li>
            <li>Profitability targets</li>
            <li>Automatic regulatory compliance</li>
            <li>Any guaranteed measurable return</li>
          </ul>

          <h2>3. External Links and Third Party Tools</h2>
          <p>
            The website may contain links or third party tools (such as Calendly) provided for convenience. Monetique <strong>is not responsible</strong> for:
          </p>
          <ul>
            <li>Content on external websites</li>
            <li>Third party privacy practices</li>
            <li>Accuracy, reliability, or security of external resources</li>
          </ul>
          <p>Access to third party websites is at the user's own risk.</p>

          <h2>4. Website Availability and Technical Limitations</h2>
          <p>While we strive to keep the site accessible and secure, Monetique does not guarantee:</p>
          <ul>
            <li>Continuous availability</li>
            <li>Absence of errors or interruptions</li>
            <li>Perfect operation across all devices and browsers</li>
            <li>Immunity from technical failures</li>
          </ul>
          <p>Periods of unavailability may occur due to hosting environments or third party services.</p>

          <h2>5. Limitation of Liability</h2>
          <p>To the fullest extent permitted by applicable law, Monetique <strong>is not liable</strong> for:</p>
          <ul>
            <li>Direct, indirect, incidental, or consequential damages</li>
            <li>Financial losses, loss of revenue, data, or business opportunities</li>
            <li>Decisions made based on advisory content or consulting</li>
          </ul>
          <p>Use of this website and services is at the sole responsibility of the user.</p>

          <h2>6. Updates to This Disclaimer</h2>
          <p>
            Monetique may update this Legal Disclaimer at any time to reflect changes in services, legal adjustments, or communication improvements.
            <strong> The most recent version will always be available on this page.</strong>
          </p>

          <p className="mt-8">
            If you have questions about this Legal Disclaimer, please contact:
            <br />
            Email: <a href="mailto:info@monetiquepro.com">info@monetiquepro.com</a>
          </p>
        </div>
      </section>
    );
  }
  return (
    <section className="legal-page disclaimer-page">
      <h1 className="main-heading">Aviso Legal</h1>

      {/* Add HTML Below */}
      <div className="disclaimer-content">
        <p>
          <strong>Última atualização:</strong> Setembro de 2026
          <br />
          <strong>Razão Social:</strong> Monetique Estratégia e Gestão Financeira Ltda.
          <br />
          <strong>Website:</strong>{" "}
          <a href="https://www.monetiquepro.com" target="_blank" rel="noopener noreferrer">
            www.monetiquepro.com
          </a>
        </p>

        <p>
          O conteúdo apresentado neste website, assim como as informações compartilhadas durante reuniões agendadas, têm o objetivo de fornecer orientação estratégica, financeira e gerencial, baseada em experiência profissional e boas práticas. A Monetique se compromete a oferecer recomendações precisas e fundamentadas para apoiar decisões mais inteligentes e um desempenho empresarial sustentável.
        </p>

        <h2>1. Natureza da Orientação Profissional</h2>
        <p>
          As informações fornecidas pela Monetique — seja pelo website, materiais escritos, consultorias ou encontros estratégicos — possuem caráter <strong>orientativo e consultivo</strong>. Embora baseadas em conhecimento técnico e experiência, tais orientações devem sempre ser analisadas considerando o contexto específico de cada cliente.
        </p>
        <p>A Monetique trabalha para oferecer recomendações de alto valor, porém:</p>
        <ul>
          <li>Não garante resultados financeiros, jurídicos ou empresariais específicos</li>
          <li>Reconhece que os resultados dependem de fatores externos e operacionais fora do nosso controle</li>
          <li>Reforça que as decisões tomadas com base nas recomendações são de responsabilidade exclusiva do cliente</li>
        </ul>
        <p>
          Sempre que uma situação exigir uma opinião formal, vinculante ou regulada (como parecer jurídico formal, análise tributária obrigatória ou emissão de documentos regulatórios), <strong>recomendamos que o cliente consulte profissionais licenciados</strong> nas áreas jurídica, fiscal, contábil ou financeira, conforme necessário.
        </p>

        <h2>2. Ausência de Garantia de Resultados</h2>
        <p>As estratégias, modelos e insights fornecidos pela Monetique são elaborados para aprimorar o processo decisório, mas os resultados podem variar conforme:</p>
        <ul>
          <li>Condições de mercado</li>
          <li>Execução operacional</li>
          <li>Fatores econômicos externos</li>
          <li>Políticas internas da empresa</li>
        </ul>
        <p>Dessa forma, a Monetique <strong>não garante</strong>:</p>
        <ul>
          <li>Crescimento ou desempenho empresarial</li>
          <li>Resultados financeiros específicos</li>
          <li>Metas de lucratividade</li>
          <li>Conformidade regulatória automática</li>
          <li>Qualquer tipo de retorno mensurável garantido</li>
        </ul>

        <h2>3. Links Externos e Ferramentas de Terceiros</h2>
        <p>
          O website pode conter links ou ferramentas de terceiros (como o Calendly) fornecidos por conveniência. A Monetique <strong>não se responsabiliza</strong> por:
        </p>
        <ul>
          <li>Conteúdos de sites externos</li>
          <li>Práticas de privacidade de terceiros</li>
          <li>Exatidão, confiabilidade ou segurança de recursos externos</li>
        </ul>
        <p>O acesso a sites de terceiros é realizado por conta e risco do usuário.</p>

        <h2>4. Disponibilidade e Limitações Técnicas do Website</h2>
        <p>Embora nos esforcemos para manter o site acessível e seguro, a Monetique não garante:</p>
        <ul>
          <li>Disponibilidade contínua</li>
          <li>Ausência de erros ou interrupções</li>
          <li>Funcionamento perfeito em todos os dispositivos e navegadores</li>
          <li>Imunidade contra falhas técnicas</li>
        </ul>
        <p>Podem ocorrer períodos de indisponibilidade devido ao ambiente de hospedagem ou serviços de terceiros.</p>

        <h2>5. Limitação de Responsabilidade</h2>
        <p>Nos limites permitidos pela legislação aplicável, a Monetique <strong>não se responsabiliza</strong> por:</p>
        <ul>
          <li>Danos diretos, indiretos, incidentais ou consequenciais</li>
          <li>Perdas financeiras, de receita, dados ou oportunidades de negócios</li>
          <li>Decisões tomadas com base em conteúdos orientativos ou consultorias</li>
        </ul>
        <p>O uso deste website e dos serviços é realizado sob responsabilidade exclusiva do usuário.</p>

        <h2>6. Atualizações deste Aviso Legal</h2>
        <p>
          A Monetique poderá atualizar este Aviso Legal a qualquer momento, para refletir mudanças nos serviços, ajustes legais ou melhorias na comunicação. 
          <strong> A versão mais recente estará sempre disponível nesta página.</strong>
        </p>

        <p className="mt-8">
          Em caso de dúvidas sobre este Aviso Legal, entre em contato:
          <br />
          Email: <a href="mailto:info@monetiquepro.com">info@monetiquepro.com</a>
        </p>
      </div>
    </section>
  );
};

export default Disclaimer;
